import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { DataFieldValue } from "@iventis/domain-model/model/dataFieldValue";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { EventBlockSystemDataFieldType } from "@iventis/domain-model/model/eventBlockSystemDataFieldType";
import { EventBlockTypeDataFields } from "@iventis/domain-model/model/eventBlockTypeDataFields";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import Color from "color";
import { compareAsc } from "date-fns";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { DataFieldValueTypes } from "@iventis/types";
import { getCustomerIdentifierFromMeta, getDataFieldValueAsText, hasObjectArrayChanged, isValidDate, setTimeOnEventDay, haveDataFieldValuesChanged } from "@iventis/utilities";

import { findEventBlockHierarchyEntitiesByCalendarLevel } from "./hierarchy.helpers";
import { SessionFormValues } from "./session.types";
import { SubEventValues } from "./sub-event.types";
import { getScheduledItemStartAndEndTimes } from "./sub-events.helpers";
import { hasRecurringRuleChanged, hasRelativeRuleChanged } from "./time-rules.helpers";
import { TimeType } from "./time-rules.types";
import { getSystemDataFieldFromEventBlock } from "./data-field-helpers";

export const sessionDefaultColour = { fill: "#E0FAFE", outline: "#A1D5DD" };
export const subSessionDefaultColour = { fill: "#B3E0E8", outline: "#A1D5DD" };

/** Gets display text for an event block using it's data field values in order */
export const getEventBlockDisplayValuesFromDataFieldValues = (
    dataFieldValues: DataFieldValue[],
    eventBlockTypeDataFields: EventBlockTypeDataFields[]
): { code: string; displayText: string } => {
    let displayText = null;
    let code = null;
    eventBlockTypeDataFields
        .filter((dataField) => dataField.namingOrder != null)
        .sort((a, b) => a.namingOrder - b.namingOrder)
        .forEach(({ dataFieldId }) => {
            const dfv = dataFieldValues.find(({ dataField }) => dataField.id === dataFieldId);
            // Event block text should not be derived from multi-select data field types
            if (dfv?.value != null && dfv?.dataField?.type !== DataFieldType.MultiSelect) {
                code = getCustomerIdentifierFromMeta(dfv);
                displayText = getDataFieldValueAsText(dfv);
            }
        });
    return { code, displayText };
};

const getSystemDataFieldIdByType = (eventBlockTypeDataFields: EventBlockTypeDataFields[], eventBlockSystemDataFieldType: EventBlockSystemDataFieldType) =>
    eventBlockTypeDataFields.find((df) => df.systemDataFieldType === eventBlockSystemDataFieldType)?.dataFieldId;

export const getSessionBoxColours = (colourIn: string) => ({
    fill: colourIn,
    outline: Color(colourIn).darken(0.25).desaturate(0.5).hex(), // This matches roughly the same colour scheme produced on the default sessions
});

const getSubSessionBoxColours = (colourIn: string) => {
    const colour = Color(colourIn);
    const darkernOrLighten = (amount: number) => (colour.isDark() ? colour.lighten(amount * 10) : colour.darken(amount));
    return {
        fill: darkernOrLighten(0.15).desaturate(0.5).hex(),
        outline: darkernOrLighten(0.25).desaturate(0.5).hex(),
    };
};

export const getCalendarSessionColour = (session: EventBlock) => {
    // The colour metadata is not required on the backend, and can default to the regular session colour
    const sessionStyleDataFieldId = getSystemDataFieldIdByType(session.type.dataFields, EventBlockSystemDataFieldType.Style);
    // The colour metadata is not required on the backend, and can default to the regular session colour
    const sessionStyleDataFieldValue = session.dataFieldValues.find(({ dataField }) => sessionStyleDataFieldId === dataField.id)?.value;
    if (sessionStyleDataFieldValue?.metaData?.colour) {
        return getSessionBoxColours(sessionStyleDataFieldValue?.metaData?.colour?.trim());
    }
    return sessionDefaultColour;
};

export const getCalendarSubSessionColour = (session: EventBlock, subSession: EventBlock) => {
    const subSessionStyleDataFieldId = getSystemDataFieldIdByType(subSession.type.dataFields, EventBlockSystemDataFieldType.Style);
    const styleDataFieldValue = subSession.dataFieldValues.find((dfv) => dfv.dataField.id === subSessionStyleDataFieldId)?.value;
    if (styleDataFieldValue?.metaData?.colour) {
        return getSubSessionBoxColours(styleDataFieldValue?.metaData?.colour?.trim());
    }
    const sessionStyleDataFieldId = getSystemDataFieldIdByType(session.type.dataFields, EventBlockSystemDataFieldType.Style);
    // The colour metadata is not required on the backend, and can default to the regular session colour
    const sessionStyleDataFieldValue = session.dataFieldValues.find(({ dataField }) => sessionStyleDataFieldId === dataField.id)?.value;
    if (sessionStyleDataFieldValue?.metaData?.colour) {
        return getSubSessionBoxColours(sessionStyleDataFieldValue?.metaData?.colour?.trim());
    }
    return subSessionDefaultColour;
};

export const getVenueLocationTypeIdByEventBlockCalendarLevel = (eventBlockHierarchy: Hierarchy<EventBlock>[], calendarLevel: number) => {
    const [eventBlock] = findEventBlockHierarchyEntitiesByCalendarLevel(eventBlockHierarchy, calendarLevel);
    if (eventBlock) {
        const eventBlockType = eventBlock.item.type;
        return eventBlockType.allowedVenueTypes[0];
    }
    return null;
};

export const getEventBlockCode = (eventBlock: EventBlock): string => {
    const dataFieldValue = getSystemDataFieldFromEventBlock(EventBlockSystemDataFieldType.Code, eventBlock);
    return getDataFieldValueAsText(dataFieldValue);
};

export const getEventBlockTypeByCalendarLevel = (eventBlockTypes: ScheduleConfigEventBlockType[], calendarLevel: number) =>
    eventBlockTypes.find((type) => type.calendarLevel === calendarLevel);

/** Checks whether an event block has been updated by performing deep equality checks, all while registering each update utilising a given callback function */
export const hasEventBlockUpdated = (existingEventBlock: EventBlock, newEventBlock: EventBlock, propertyUpdatedCallback?: (property: keyof EventBlock) => void): boolean => {
    let hasUpdated = false;
    Object.keys(createEmptyEvent() as EventBlock).forEach((key: keyof EventBlock) => {
        const existingValue = existingEventBlock[key];
        if (JSON.stringify(newEventBlock[key]) !== JSON.stringify(existingValue) && !(newEventBlock[key] == null && existingValue == null)) {
            const registerUpdate = () => {
                propertyUpdatedCallback?.(key);
                hasUpdated = true;
            };
            switch (key) {
                // For certain properties, JSON stringify may appear to show a difference when there might not be one, so we perform manual checks
                case "dataFieldValues": {
                    // Using `existingEventBlock[key]` here instead of `value` so we can avoid the need to cast the `value` type
                    if (haveDataFieldValuesChanged(existingEventBlock[key], newEventBlock[key])) {
                        registerUpdate();
                    }
                    break;
                }
                case "relativeEventRules": {
                    if (hasObjectArrayChanged(existingEventBlock[key], newEventBlock[key], hasRelativeRuleChanged)) {
                        registerUpdate();
                    }
                    break;
                }
                case "recurringEventRules": {
                    if (hasObjectArrayChanged(existingEventBlock[key], newEventBlock[key], hasRecurringRuleChanged)) {
                        registerUpdate();
                    }
                    break;
                }
                case "type":
                case "relativeEventRuleId":
                    // You cannot edit these properties, so we ignore any differences between old and new as they are not significant
                    break;
                case "startAt":
                case "endAt":
                    // If value has switched to null, switched from null or not equal, then register update
                    if (!isValidDate(existingValue as Date) || !isValidDate(newEventBlock[key]) || compareAsc(existingValue as Date, newEventBlock[key]) !== 0) {
                        registerUpdate();
                    }
                    break;
                default: {
                    // For the rest, we can assume the JSON stringify check is sufficient
                    registerUpdate();
                }
            }
        }
    });
    return hasUpdated;
};

const createEmptyEvent: () => Required<EventBlock> = () => ({
    name: undefined,
    venueLocation: undefined,
    startAt: undefined,
    endAt: undefined,
    zeroAt: undefined,
    recurringEventRules: undefined,
    dataFieldValues: [],
    type: undefined,
    parentId: undefined,
    scheduleVersionId: undefined,
    id: undefined,
    relativeEventRules: undefined,
    relativeEventRuleId: undefined,
    startTimeType: undefined,
    estimatedTime: undefined,
    description: undefined,
});

export const isEmptyEvent = (eventBlock: EventBlock) =>
    Object.entries(createEmptyEvent())
        .filter(([key]) => key !== "type")
        .every(([key, value]) => eventBlock[key] === value || (Array.isArray(eventBlock[key]) && Array.isArray(value) && value.length === 0 && eventBlock[key].length === 0));

/** Creates a new blank event hierarchy */
export const createEventHierarchy = (eventType?: ScheduleConfigEventBlockType): Hierarchy<EventBlock> => {
    const hierarchy: Hierarchy<EventBlock> = { item: createEmptyEvent(), children: [] };
    hierarchy.item.type = eventType;
    return hierarchy;
};

/** Maps the form data field values (where the data field id is the key and the value is the data field value) to our domain model */
const mapFormDataFieldValuesToDomain = (dataFieldValues: { [id: string]: DataFieldValueTypes }, dataFields: DataField[]) =>
    Object.keys(dataFieldValues).reduce((arr, key) => (key == null ? [] : [...arr, { dataField: dataFields.find((df) => df.id === key), value: dataFieldValues[key] }]), []);

export const mapSessionFormValuesToEventBlock = (
    formValues: SessionFormValues,
    existingEventBlock: EventBlock,
    sessionCodeDataField: DataField,
    timezone: string,
    dataFields: DataField[]
) => {
    const { sessionDay, scheduleDaysMap, startTime, endTime, zeroTime, zeroTimeDisabled, sessionCode, dataFieldValues, scheduleVersion, venueLocation } = formValues;
    // Until we move this hard coded data field value in with the rest, we must append here
    dataFieldValues[sessionCodeDataField.id] = `${sessionCode.sessionCodePrefix}${sessionCode.sessionCodeDigit}`;
    const emptyEvent = createEmptyEvent();
    const mappedEventBlock: EventBlock = {} as EventBlock;
    Object.keys(emptyEvent).forEach((key: keyof EventBlock) => {
        switch (key) {
            case "id":
                mappedEventBlock[key] = existingEventBlock?.id;
                break;
            case "startAt":
                mappedEventBlock[key] = startTime == null || startTime === "" ? null : setTimeOnEventDay(scheduleDaysMap.get(Number(sessionDay)), startTime, timezone);
                break;
            case "endAt":
                mappedEventBlock[key] = endTime == null || endTime === "" ? null : setTimeOnEventDay(scheduleDaysMap.get(Number(sessionDay)), endTime, timezone);
                break;
            case "zeroAt":
                mappedEventBlock[key] = zeroTimeDisabled ? null : zeroTime;
                break;
            case "scheduleVersionId":
                mappedEventBlock[key] = scheduleVersion.id;
                break;
            case "venueLocation":
                mappedEventBlock[key] = venueLocation?.item;
                break;
            case "dataFieldValues":
                mappedEventBlock[key] = mapFormDataFieldValuesToDomain(dataFieldValues, dataFields);
                break;
            default:
                // eslint-disable-next-line no-prototype-builtins
                mappedEventBlock[key as string] = formValues.hasOwnProperty(key) ? formValues[key] : existingEventBlock?.[key];
                break;
        }
    });
    return mappedEventBlock;
};

export const mapSubEventFormValuesToEventBlock = (
    formValues: SubEventValues,
    existingEventBlock: EventBlock,
    sessionStartDate: Date,
    timezone: string,
    dataFields: DataField[]
) => {
    const { startTime, endTime, dataFieldValues, timeType, relativeEventRules } = formValues;
    const emptyEvent = createEmptyEvent();
    const mappedEventBlock: EventBlock = {} as EventBlock;
    let startAndEnd: { startAt: Date; endAt: Date } = null;
    // When no time has been set yet, the start and end time will be an empty string in the form, so validate the exist before trying to manipulate them.
    if (startTime != null && startTime !== "" && endTime != null && endTime !== "") {
        startAndEnd = getScheduledItemStartAndEndTimes(sessionStartDate, timezone, startTime, endTime);
    }
    Object.keys(emptyEvent).forEach((key: keyof EventBlock) => {
        switch (key) {
            case "id":
                mappedEventBlock[key] = existingEventBlock?.id;
                break;
            case "startAt":
                mappedEventBlock[key] = startAndEnd?.startAt;
                break;
            case "endAt":
                mappedEventBlock[key] = startAndEnd?.endAt;
                break;
            case "dataFieldValues":
                mappedEventBlock[key] = mapFormDataFieldValuesToDomain(dataFieldValues, dataFields);
                break;
            case "relativeEventRules":
                mappedEventBlock[key] = timeType === TimeType.Relative ? relativeEventRules : null;
                break;
            default:
                // eslint-disable-next-line no-prototype-builtins
                mappedEventBlock[key as string] = formValues.hasOwnProperty(key) ? formValues[key] : existingEventBlock?.[key];
                break;
        }
    });
    return mappedEventBlock;
};

/** Returns true if the event block appears on the calendar */
export const isCalendarEventBlock = (type: ScheduleConfigEventBlockType) => typeof type.calendarLevel === "number";
